import { resolveSchema, resolveDomainEvent } from './workflowSpec.js'
import { computeMidString, getLastCardSortkey } from './sortkey.js'
import {
  buildSchemaField,
  resolveEntityRef,
  resolveFieldRefs,
  resolveFieldUpdateRefs,
  applyNestedFieldUpdates,
  normalizeDescription,
  buildSchemaRef,
  unionSchemaFields
} from './schemaFields.js'
import { sanitizeCardDescription } from './sanitize.js'
import { buildCardDeletes } from './cardMutations.js'
import { DomainModelRole } from '../constants/cardTypes.js'
import { SchemaType, MAX_DESCRIPTION_LENGTH, MAX_FIELD_DESCRIPTION_LENGTH } from '../constants/schema.js'

const generateId = () => globalThis.crypto.randomUUID()

const findAllReadModelCardsBySchemaId = (domainEvents, schemaId, cardTypesMap) => {
  const matches = []

  for (let eventIndex = 0; eventIndex < domainEvents.length; eventIndex++) {
    const event = domainEvents[eventIndex]
    const cards = event.cards || []
    for (let cardIndex = 0; cardIndex < cards.length; cardIndex++) {
      const card = cards[cardIndex]
      const cardType = cardTypesMap[card.cardTypeId]
      if (cardType?.domainModelRole === DomainModelRole.READ_MODEL && card.schemaId === schemaId) {
        matches.push({ card, event, eventIndex, cardIndex })
      }
    }
  }

  return matches
}

const resolveUpdateTriggerRefs = (refs, workflow, parameterName) => {
  if (!Array.isArray(refs)) {
    return { error: `${parameterName} must be an array of domain event schema refs` }
  }

  const schemaIds = []
  for (const ref of refs) {
    if (typeof ref !== 'string') {
      return { error: `${parameterName} must contain only domain event schema refs` }
    }
    const schema = resolveSchema(ref, workflow)
    if (!schema || schema.type !== SchemaType.DOMAIN_EVENT) {
      return { error: `Domain event schema not found for ref: ${ref}` }
    }
    if (!schemaIds.includes(schema.id)) {
      schemaIds.push(schema.id)
    }
  }

  return { schemaIds }
}

export const buildCreateReadModelsMutation = ({ workflow, workflowId, readModels, cardTypes }) => {
  if (!Array.isArray(readModels) || readModels.length === 0) {
    return { error: 'readModels is required and must be a non-empty array' }
  }

  const cardTypesList = cardTypes || []
  const cardTypesMap = Object.fromEntries(cardTypesList.map((cardType) => [cardType.id, cardType]))
  const readModelCardType = cardTypesList.find((cardType) => cardType.domainModelRole === DomainModelRole.READ_MODEL)
  if (!readModelCardType) {
    return { error: 'Read model card type not found in this workflow' }
  }

  // nameToState: per read-model name, either a new schema being built or an existing schema being merged into.
  const nameToState = new Map()
  // batchEventNamePairs dedupes (eventId, name) so the same read model isn't attached twice to one event in a batch.
  const batchEventNamePairs = new Set()
  const pendingCards = []

  const domainEvents = workflow.domainEvents || []
  let lastSortkey = getLastCardSortkey(domainEvents)

  for (const entry of readModels) {
    const { domainEvent, name, description, entity, cardinality, fields, updateTriggers } = entry || {}

    if (!domainEvent) {
      return { error: 'Each read model must have a domainEvent ref' }
    }
    if (!name || typeof name !== 'string' || name.trim() === '') {
      return { error: 'Each read model must have a non-empty name' }
    }
    if (!cardinality) {
      return { error: `Each read model must have a cardinality (read model "${name}")` }
    }

    const normalizedDescription = normalizeDescription(description, MAX_DESCRIPTION_LENGTH)
    if (normalizedDescription.error) {
      return { error: `${normalizedDescription.error} (read model "${name}")` }
    }

    const matchedEvent = resolveDomainEvent(domainEvent, workflow)
    if (!matchedEvent) {
      return { error: `Domain event not found for ref: ${domainEvent}` }
    }

    const sanitizedName = sanitizeCardDescription(name)
    const pairKey = `${matchedEvent.id}::${sanitizedName}`
    if (batchEventNamePairs.has(pairKey)) {
      return { error: `Read model "${sanitizedName}" is targeted at event "${matchedEvent.description}" more than once in the batch` }
    }
    batchEventNamePairs.add(pairKey)

    const resolvedEntityId = entity ? resolveEntityRef(entity, workflow) : undefined

    let resolvedTriggerIds = []
    if (updateTriggers !== undefined) {
      const resolvedTriggers = resolveUpdateTriggerRefs(updateTriggers, workflow, 'updateTriggers')
      if (resolvedTriggers.error) {
        return { error: `${resolvedTriggers.error} (read model "${name}")` }
      }
      resolvedTriggerIds = resolvedTriggers.schemaIds
    }

    let state = nameToState.get(sanitizedName)
    if (!state) {
      const existingSchema = (workflow.schemas || []).find((schema) => schema.name === sanitizedName && schema.type === SchemaType.QUERY)
      // A domain event schema can be a read model too: the step then reads that event's payload.
      const domainEventSchema = existingSchema
        ? null
        : (workflow.schemas || []).find((schema) => schema.name === sanitizedName && schema.type === SchemaType.DOMAIN_EVENT)
      if (domainEventSchema) {
        state = {
          mode: 'existing',
          isDomainEvent: true,
          name: sanitizedName,
          schema: domainEventSchema,
          schemaId: domainEventSchema.id,
          schemaIndex: workflow.schemas.findIndex((schema) => schema.id === domainEventSchema.id),
          mergedFields: domainEventSchema.fields || [],
          existingTriggerIds: [],
          triggerIds: []
        }
      } else if (existingSchema) {
        state = {
          mode: 'existing',
          name: sanitizedName,
          schemaId: existingSchema.id,
          schemaIndex: workflow.schemas.findIndex((schema) => schema.id === existingSchema.id),
          existingVersion: existingSchema.version || null,
          existingFields: existingSchema.fields || [],
          existingCardinality: existingSchema.cardinality,
          mergedFields: existingSchema.fields || [],
          cardinality: existingSchema.cardinality,
          entityId: existingSchema.entityId,
          description: existingSchema.description,
          existingTriggerIds: existingSchema.domainEventIds || [],
          triggerIds: [...(existingSchema.domainEventIds || [])]
        }
      } else {
        state = {
          mode: 'new',
          name: sanitizedName,
          schemaId: generateId(),
          mergedFields: [],
          cardinality,
          entityId: resolvedEntityId,
          description: normalizedDescription.value,
          existingTriggerIds: [],
          triggerIds: []
        }
      }
      nameToState.set(sanitizedName, state)
    }

    if (state.mode === 'existing') {
      const alreadyLinked = (matchedEvent.cards || []).some((card) => {
        return cardTypesMap[card.cardTypeId]?.domainModelRole === DomainModelRole.READ_MODEL &&
          card.schemaId === state.schemaId
      })
      if (alreadyLinked) {
        return { error: `Read model "${sanitizedName}" is already attached to event "${matchedEvent.description}"` }
      }
    }
    if (state.isDomainEvent && updateTriggers !== undefined) {
      return { error: `"${sanitizedName}" is a domain event schema, so it takes no updateTriggers` }
    }

    const resolvedFields = resolveFieldRefs(fields, workflow)
    const fieldType = state.isDomainEvent ? SchemaType.DOMAIN_EVENT : SchemaType.QUERY
    const incomingFields = (resolvedFields || []).map((field) => buildSchemaField(field, fieldType))
    state.mergedFields = unionSchemaFields(state.mergedFields, incomingFields)
    if (cardinality) {
      state.cardinality = cardinality
    }
    if (resolvedEntityId && !state.entityId) {
      state.entityId = resolvedEntityId
    }
    if (normalizedDescription.value && !state.description) {
      state.description = normalizedDescription.value
    }
    for (const triggerId of resolvedTriggerIds) {
      if (!state.triggerIds.includes(triggerId)) {
        state.triggerIds.push(triggerId)
      }
    }

    // A Read Model card with no schema is the state validation reports as "no entity
    // reference" — link it rather than adding a second card beside it.
    const unlinkedCard = (matchedEvent.cards || []).find((card) => {
      return cardTypesMap[card.cardTypeId]?.domainModelRole === DomainModelRole.READ_MODEL && !card.schemaId
    })

    if (unlinkedCard) {
      pendingCards.push({
        eventId: matchedEvent.id,
        description: unlinkedCard.description,
        schemaId: state.schemaId,
        sortkey: unlinkedCard.sortkey,
        existingCard: unlinkedCard,
        cardIndex: (matchedEvent.cards || []).indexOf(unlinkedCard)
      })

      continue
    }

    const sortkey = computeMidString(lastSortkey, '')
    lastSortkey = sortkey

    pendingCards.push({
      eventId: matchedEvent.id,
      description: sanitizedName,
      schemaId: state.schemaId,
      sortkey
    })
  }

  const schemas = []
  for (const state of nameToState.values()) {
    if (state.mode === 'new') {
      schemas.push({
        id: state.schemaId,
        name: state.name,
        description: state.description,
        type: SchemaType.QUERY,
        cardinality: state.cardinality,
        entityId: state.entityId,
        domainEventIds: state.triggerIds.length > 0 ? state.triggerIds : undefined,
        fields: state.mergedFields,
        index: -1
      })
    } else if (state.isDomainEvent) {
      // Only the fields: a read model's entity or description means something else on a domain event
      // schema, whose entity also decides which bounded context it is exported under.
      if (JSON.stringify(state.mergedFields) !== JSON.stringify(state.schema.fields || [])) {
        schemas.push({
          ...state.schema,
          fields: state.mergedFields,
          index: state.schemaIndex,
          expectedVersion: state.schema.version || null
        })
      }
    } else {
      const fieldsChanged = JSON.stringify(state.mergedFields) !== JSON.stringify(state.existingFields)
      const cardinalityChanged = state.cardinality !== state.existingCardinality
      const triggersChanged = state.triggerIds.length !== state.existingTriggerIds.length
      if (fieldsChanged || cardinalityChanged || triggersChanged) {
        schemas.push({
          id: state.schemaId,
          name: state.name,
          description: state.description,
          type: SchemaType.QUERY,
          cardinality: state.cardinality,
          entityId: state.entityId,
          domainEventIds: triggersChanged ? state.triggerIds : undefined,
          fields: state.mergedFields,
          index: state.schemaIndex,
          expectedVersion: state.existingVersion
        })
      }
    }
  }

  // schemaInput is null when every targeted read model already exists with no field/cardinality change —
  // only the card links are added in that case.
  const schemaInput = schemas.length > 0
    ? {
        id: workflowId,
        expectedVersion: workflow.version,
        schemas,
        updateNote: {
          action: 'created read models',
          target: schemas.map((schema) => schema.name).join(', ')
        }
      }
    : null

  const cards = pendingCards.map(({ eventId, description, schemaId, sortkey, existingCard, cardIndex }) => {
    const eventIndex = domainEvents.findIndex((event) => event.id === eventId)

    if (existingCard) {
      return {
        cardId: existingCard.id,
        cardIndex,
        expectedVersion: existingCard.version,
        description,
        sortkey,
        cardTypeId: existingCard.cardTypeId,
        done: existingCard.done ?? false,
        schemaId,
        boundedContext: existingCard.boundedContext,
        cardinality: existingCard.cardinality,
        releaseNo: existingCard.releaseNo,
        eventId,
        eventIndex
      }
    }

    return {
      description,
      done: false,
      cardTypeId: readModelCardType.id,
      releaseNo: null,
      sortkey,
      boundedContext: null,
      schemaId,
      cardIndex: -1,
      eventId,
      eventIndex
    }
  })

  // cardInput's expectedVersion is threaded in by the caller after schemaInput persists (if any).
  const cardInput = {
    workflowId,
    updateNote: {
      action: 'added read model cards',
      target: [...nameToState.keys()].join(', ')
    },
    cards
  }

  // Cards outnumber schemas when one read model is attached to several events, and an
  // existing read model gets linked rather than created — neither counts as created.
  const states = [...nameToState.values()]
  const createdCount = states.filter((state) => state.mode === 'new').length
  const linkedNames = states.filter((state) => state.mode === 'existing').map((state) => state.name)
  const linkedNote = linkedNames.length > 0
    ? ` Linked existing read model${linkedNames.length === 1 ? '' : 's'}: ${linkedNames.join(', ')}.`
    : ''

  // "0 created" on a pure link reads as failure and invites a pointless retry.
  const message = createdCount === 0 && linkedNames.length > 0
    ? `No new read models needed.${linkedNote}`
    : `${createdCount} ${createdCount === 1 ? 'read model' : 'read models'} created successfully.${linkedNote}`

  return {
    schemaInput,
    cardInput,
    message,
    refs: states.map((state) => ({ name: state.name, ref: buildSchemaRef(state.isDomainEvent ? 'domainEvents' : 'queries', state.name) }))
  }
}

export const buildUpdateReadModelsMutation = ({ workflow, workflowId, readModels, cardTypes }) => {
  if (!Array.isArray(readModels) || readModels.length === 0) {
    return { error: 'readModels is required and must be a non-empty array' }
  }

  const cardTypesMap = Object.fromEntries((cardTypes || []).map((cardType) => [cardType.id, cardType]))

  const claimedSchemaIds = new Set()
  const claimedNewNames = new Set()
  const schemaInputs = []
  const changeSummaries = []
  const refsOut = []
  const pendingRenames = []

  for (const update of readModels) {
    const {
      readModel: readModelRef,
      name,
      description,
      cardinality,
      entity,
      addFields: rawAdds,
      updateFields: rawUpdates,
      removeFields,
      addUpdateTriggers,
      removeUpdateTriggers
    } = update || {}

    if (!readModelRef) {
      return { error: 'Each item must include a readModel ref' }
    }

    const resolved = resolveSchema(readModelRef, workflow, cardTypesMap)
    if (!resolved) {
      return { error: `Read model not found for ref: ${readModelRef}` }
    }
    if (resolved.type !== SchemaType.QUERY) {
      return { error: `"${readModelRef}" is not a read model. A domain event schema is changed with update_domain_event_schemas, an entity with update_entities` }
    }
    const schemaId = resolved.id

    if (claimedSchemaIds.has(schemaId)) {
      return { error: `Read model "${readModelRef}" appears more than once in the batch` }
    }
    claimedSchemaIds.add(schemaId)

    const schemaIndex = (workflow.schemas || []).findIndex((schema) => schema.id === schemaId)
    if (schemaIndex === -1) {
      return { error: `Read model not found for ref: ${readModelRef}` }
    }

    const resolvedEntityId = entity ? resolveEntityRef(entity, workflow) : undefined
    const addFields = resolveFieldRefs(rawAdds, workflow)
    const updateFields = resolveFieldUpdateRefs(rawUpdates, workflow)

    const schema = workflow.schemas[schemaIndex]
    const currentFields = [...(schema.fields || [])]
    const changes = []

    if (removeFields && removeFields.length > 0) {
      for (const fieldName of removeFields) {
        const fieldIndex = currentFields.findIndex((field) => field.name === fieldName)
        if (fieldIndex === -1) {
          return { error: `Field "${fieldName}" not found in read model "${schema.name}"` }
        }
        currentFields.splice(fieldIndex, 1)
        changes.push(`removed "${fieldName}"`)
      }
    }

    if (updateFields && updateFields.length > 0) {
      for (const { fieldName, updates } of updateFields) {
        const fieldIndex = currentFields.findIndex((field) => field.name === fieldName)
        if (fieldIndex === -1) {
          return { error: `Field "${fieldName}" not found in read model "${schema.name}"` }
        }

        if (updates.name && updates.name !== fieldName) {
          const collision = currentFields.find((field) => field.name === updates.name)
          if (collision) {
            return { error: `Field "${updates.name}" already exists in read model "${schema.name}"` }
          }
        }

        const updatedField = { ...currentFields[fieldIndex] }

        if (updates.name !== undefined) {
          updatedField.name = updates.name
        }
        if (updates.description !== undefined) {
          const normalized = normalizeDescription(updates.description, MAX_FIELD_DESCRIPTION_LENGTH)
          if (normalized.error) {
            return { error: normalized.error }
          }
          if (normalized.value) {
            updatedField.description = normalized.value
          } else {
            delete updatedField.description
          }
        }
        if (updates.isFilter !== undefined) {
          if (updates.isFilter) {
            updatedField.type = 'filter'
          } else {
            delete updatedField.type
          }
        }
        if (updates.computed !== undefined) {
          if (updates.computed) {
            updatedField.computed = true
          } else {
            delete updatedField.computed
          }
        }
        if (updates.relatedEntityId !== undefined) {
          updatedField.relatedEntityId = updates.relatedEntityId
          if (!updatedField.relatedEntityId) {
            delete updatedField.fields
          }
        }
        if (updates.cardinality !== undefined) {
          updatedField.cardinality = updates.cardinality
        }
        if (updates.fields !== undefined) {
          const nested = applyNestedFieldUpdates(updatedField.fields, updates.fields)
          if (nested && nested.length > 0) {
            updatedField.fields = nested
          } else {
            delete updatedField.fields
          }
        }

        currentFields[fieldIndex] = updatedField
        changes.push(`updated "${fieldName}"`)
      }
    }

    if (addFields && addFields.length > 0) {
      for (const field of addFields) {
        if (currentFields.find((existing) => existing.name === field.name)) {
          return { error: `Field "${field.name}" already exists in read model "${schema.name}"` }
        }
        currentFields.push(buildSchemaField(field, SchemaType.QUERY))
        changes.push(`added "${field.name}"`)
      }
    }

    const updatedSchema = {
      ...schema,
      fields: currentFields
    }

    const sanitizedName = name !== undefined ? sanitizeCardDescription(name) : undefined
    if (sanitizedName !== undefined && sanitizedName !== schema.name) {
      const collision = workflow.schemas.find((other) => other.name === sanitizedName && other.type === SchemaType.QUERY && other.id !== schemaId)
      if (collision) {
        return { error: `Read model "${sanitizedName}" already exists` }
      }
      if (claimedNewNames.has(sanitizedName)) {
        return { error: `Read model "${sanitizedName}" is set by more than one entry in the batch` }
      }
      claimedNewNames.add(sanitizedName)
      updatedSchema.name = sanitizedName
      changes.push(`renamed to "${sanitizedName}"`)
      pendingRenames.push({ schemaId, newName: sanitizedName })
    }

    if (description !== undefined) {
      const normalized = normalizeDescription(description, MAX_DESCRIPTION_LENGTH)
      if (normalized.error) {
        return { error: normalized.error }
      }
      if (normalized.value) {
        updatedSchema.description = normalized.value
        changes.push('updated description')
      } else {
        updatedSchema.description = null
        changes.push('removed description')
      }
    }

    if (entity !== undefined) {
      updatedSchema.entityId = resolvedEntityId
      changes.push('updated entity')
    }

    if (cardinality !== undefined && cardinality !== schema.cardinality) {
      updatedSchema.cardinality = cardinality
      changes.push(`cardinality set to "${cardinality}"`)
    }

    if (removeUpdateTriggers !== undefined || addUpdateTriggers !== undefined) {
      const storedTriggerIds = schema.domainEventIds || []
      // Deleting a domain event schema leaves its id behind on the read models that listed it.
      let triggerIds = storedTriggerIds.filter((triggerId) => workflow.schemas.some((other) => other.id === triggerId))

      if (removeUpdateTriggers !== undefined) {
        const resolved = resolveUpdateTriggerRefs(removeUpdateTriggers, workflow, 'removeUpdateTriggers')
        if (resolved.error) {
          return { error: `${resolved.error} (read model "${schema.name}")` }
        }
        for (const triggerId of resolved.schemaIds) {
          if (!triggerIds.includes(triggerId)) {
            const triggerName = workflow.schemas.find((other) => other.id === triggerId).name
            return { error: `Read model "${schema.name}" is not triggered by "${triggerName}"` }
          }
        }
        triggerIds = triggerIds.filter((triggerId) => !resolved.schemaIds.includes(triggerId))
      }

      if (addUpdateTriggers !== undefined) {
        const resolved = resolveUpdateTriggerRefs(addUpdateTriggers, workflow, 'addUpdateTriggers')
        if (resolved.error) {
          return { error: `${resolved.error} (read model "${schema.name}")` }
        }
        for (const triggerId of resolved.schemaIds) {
          if (!triggerIds.includes(triggerId)) {
            triggerIds.push(triggerId)
          }
        }
      }

      const triggersChanged = triggerIds.length !== storedTriggerIds.length ||
        triggerIds.some((triggerId, position) => triggerId !== storedTriggerIds[position])

      if (triggersChanged) {
        const triggerNames = triggerIds.map((triggerId) => workflow.schemas.find((other) => other.id === triggerId).name)
        // null, not []: only null makes the resolver remove the attribute.
        updatedSchema.domainEventIds = triggerIds.length > 0 ? triggerIds : null
        changes.push(triggerNames.length > 0 ? `triggered by ${triggerNames.join(', ')}` : 'cleared update triggers')
      }
    }

    if (changes.length === 0) {
      return { error: `No changes specified for "${schema.name}"` }
    }

    schemaInputs.push({
      ...updatedSchema,
      index: schemaIndex,
      expectedVersion: schema.version || null
    })
    changeSummaries.push(`"${schema.name}": ${changes.join(', ')}`)
    refsOut.push({
      name: updatedSchema.name,
      ref: buildSchemaRef('queries', updatedSchema.name)
    })
  }

  const schemaInput = {
    id: workflowId,
    expectedVersion: workflow.version,
    schemas: schemaInputs,
    updateNote: {
      action: 'updated read models',
      target: schemaInputs.map((schema) => schema.name).join(', ')
    }
  }

  // Renaming a read model also renames every linked Read Model card (a query can be reused across events).
  // cardInput's expectedVersion is threaded in by the caller after schemaInput persists.
  let cardInput = null

  if (pendingRenames.length > 0) {
    const cardsToUpdate = []

    for (const { schemaId, newName } of pendingRenames) {
      const matches = findAllReadModelCardsBySchemaId(workflow.domainEvents || [], schemaId, cardTypesMap)
      for (const match of matches) {
        cardsToUpdate.push({
          cardId: match.card.id,
          cardIndex: match.cardIndex,
          expectedVersion: match.card.version,
          description: newName,
          sortkey: match.card.sortkey,
          cardTypeId: match.card.cardTypeId,
          done: match.card.done ?? false,
          schemaId: match.card.schemaId,
          boundedContext: match.card.boundedContext,
          releaseNo: match.card.releaseNo,
          eventId: match.event.id,
          eventIndex: match.eventIndex
        })
      }
    }

    if (cardsToUpdate.length > 0) {
      cardInput = {
        workflowId,
        updateNote: {
          action: 'renamed read model cards',
          target: pendingRenames.map((rename) => rename.newName).join(', ')
        },
        cards: cardsToUpdate
      }
    }
  }

  const updatedCount = schemaInput.schemas.length

  return {
    schemaInput,
    cardInput,
    changeSummaries,
    message: `${updatedCount} ${updatedCount === 1 ? 'read model' : 'read models'} updated: ${changeSummaries.join(' | ')}`,
    refs: refsOut
  }
}

export const buildDeleteReadModelMutation = ({ workflow, workflowId, readModel, cardTypes }) => {
  if (!readModel) {
    return { error: 'Read model ref is required' }
  }

  const cardTypesMap = Object.fromEntries((cardTypes || []).map((cardType) => [cardType.id, cardType]))

  const resolved = resolveSchema(readModel, workflow, cardTypesMap)
  if (!resolved) {
    return { error: `Read model not found for ref: ${readModel}` }
  }
  if (resolved.type !== SchemaType.QUERY) {
    return { error: `"${readModel}" is not a read model, so nothing was deleted. A domain event schema is deleted with delete_domain_event_schema, an entity with delete_entity` }
  }
  const schemaId = resolved.id

  const schemaIndex = (workflow.schemas || []).findIndex((schema) => schema.id === schemaId)
  if (schemaIndex === -1) {
    return { error: 'Read model not found' }
  }

  const schema = workflow.schemas[schemaIndex]
  const matches = findAllReadModelCardsBySchemaId(workflow.domainEvents || [], schemaId, cardTypesMap)

  // Every linked Read Model card is deleted first (a query can be reused across events);
  // each card delete and the schema delete are version-threaded by the caller.
  const cardDeletes = buildCardDeletes(workflowId, matches)

  const schemaInput = {
    id: workflowId,
    deletedSchemas: [{ deleteIndex: schemaIndex, deleteId: schemaId }],
    updateNote: {
      action: 'deleted read model',
      target: schema.name
    }
  }

  return { cardDeletes, schemaInput, schema, message: `Read model "${schema.name}" deleted successfully` }
}
